import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings, digits } from "@/context/SettingsContext";
import { Save, Phone, MessageCircle, Info, Mail } from "lucide-react";

/**
 * Admin Contact & WhatsApp Settings
 * ---------------------------------
 * Edits `settings.phone` (customer-facing contact number),
 * `settings.whatsapp_number` (all WhatsApp CTA links across the site) and
 * `settings.email` (customer-facing email). Fully dynamic — no hard-coded fallbacks anywhere.
 * Uses PATCH /api/admin/settings which was already wired to the store settings doc.
 */
export default function AdminContactSettings() {
  const { refresh } = useSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ phone: "", whatsapp_number: "", email: "" });
  const [original, setOriginal] = useState({ phone: "", whatsapp_number: "", email: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        const next = { phone: data.phone || "", whatsapp_number: data.whatsapp_number || "", email: data.email || "" };
        setForm(next);
        setOriginal(next);
      } catch { toast.error("Could not load contact settings"); }
      finally { setLoading(false); }
    })();
  }, []);

  const waDigits = digits(form.whatsapp_number);
  const waValid = waDigits.length >= 10;
  const phoneValid = form.phone.trim().length >= 6;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const dirty = form.phone !== original.phone || form.whatsapp_number !== original.whatsapp_number || form.email !== original.email;

  const save = async () => {
    if (!phoneValid) { toast.error("Enter a valid contact number"); return; }
    if (!waValid) { toast.error("WhatsApp number must have at least 10 digits (with country code)"); return; }
    if (!emailValid) { toast.error("Enter a valid email address"); return; }
    setSaving(true);
    try {
      const payload = {
        phone: form.phone.trim(),
        whatsapp_number: waDigits, // always store digits-only so wa.me works everywhere
        email: form.email.trim(),
      };
      await api.patch("/admin/settings", payload);
      setOriginal(payload);
      setForm(payload);
      await refresh();
      toast.success("Contact, WhatsApp & Email updated across the site");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-contact-settings">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl">Contact &amp; WhatsApp</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Update the customer-facing contact number, WhatsApp number and email. Every wa.me link, "Chat on WhatsApp" button, phone display and email link on the website will update automatically — no code changes needed.
          </p>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving || !phoneValid || !waValid || !emailValid}
          className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 justify-center ${dirty && phoneValid && waValid && emailValid ? "btn-gold" : "border hairline text-muted-foreground opacity-60"}`}
          data-testid="save-contact-btn"
        >
          <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
          {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
        </button>
      </div>

      <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-4 text-sm flex gap-3">
        <Info className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-emerald-900">
          <div className="font-medium mb-1">Where these are used</div>
          <ul className="list-disc pl-4 text-[13px] space-y-0.5">
            <li><b>Contact Number</b> — shown on the Contact page and inside the customer-facing header/footer sections that print a phone number.</li>
            <li><b>WhatsApp Number</b> — powers the floating WhatsApp button, footer WhatsApp icon, PDP "Enquire on WhatsApp", Checkout payment help, order-success support link + post-payment redirect, Deepavali / Occasions bulk enquiry, Home reviews CTA and Admin Orders "Message customer" links.</li>
            <li><b>Email</b> — shown on the Contact page and used for customer email links.</li>
            <li>If a field is left unsaved/empty, the related buttons hide automatically — the site never falls back to an old number.</li>
            <li>Save WhatsApp with country code (India: 91xxxxxxxxxx). Non-digits like +, spaces and dashes are stripped automatically.</li>
          </ul>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <label className="block border hairline rounded-xl bg-white p-4 md:p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            <Phone className="w-4 h-4 gold-text" strokeWidth={1.5} /> Contact Number
          </div>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+91 97112 52903"
            className="w-full border hairline rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#B8860B]"
            data-testid="contact-phone-input"
          />
          <div className="mt-2 text-[11px] text-muted-foreground">Shown to customers on the Contact page and other contact sections. Include country code and spacing as you'd like it displayed.</div>
        </label>

        <label className="block border hairline rounded-xl bg-white p-4 md:p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            <MessageCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /> WhatsApp Number
          </div>
          <input
            type="tel"
            value={form.whatsapp_number}
            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
            placeholder="919711252903"
            className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#B8860B] font-mono ${form.whatsapp_number && !waValid ? "border-destructive" : "hairline"}`}
            data-testid="contact-whatsapp-input"
          />
          <div className="mt-2 text-[11px] text-muted-foreground">Digits-only (with country code) for wa.me links. India: 91xxxxxxxxxx. Currently normalised: <span className="font-mono text-espresso">{waDigits || "—"}</span></div>
        </label>
        <label className="block border hairline rounded-xl bg-white p-4 md:p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
            <Mail className="w-4 h-4 gold-text" strokeWidth={1.5} /> Email
          </div>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="info@adiyogihandicraft.com"
            className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#B8860B] ${form.email && !emailValid ? "border-destructive" : "hairline"}`}
            data-testid="contact-email-input"
          />
          <div className="mt-2 text-[11px] text-muted-foreground">Customer-facing email shown on the Contact page and used in email links.</div>
        </label>
      </div>

      {/* Live preview of the wa.me link */}
      <div className="border hairline rounded-xl bg-white p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Live WhatsApp Link Preview</div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={waDigits ? `https://wa.me/${waDigits}` : "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 hover:underline break-all text-sm font-mono"
            data-testid="wa-preview-link"
          >
            https://wa.me/{waDigits || "—"}
          </a>
          <span className="text-[10px] text-muted-foreground">(what customers open when they tap any WhatsApp button)</span>
        </div>
      </div>
    </div>
  );
}
